import React, { useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import { LiveDetailScreen, GuestRoomScreen } from './lazy';
import { analytics } from '../services/analytics';
import { useAuth } from '../context/AuthContext';
import { resolveBroadcastGuestEntry } from '../utils/broadcastGuestEntry';

/**
 * `/live/:id` route entry. Picks between the public announcement page and the
 * cohost room based on `?preview={token}`.
 *
 * CORE-528 — this branch used to live inside `LiveDetailScreen` as a mount
 * effect that dispatched `navigation.replace('GuestRoom', …)`. On a *cold*
 * load — which is the only way a guest ever arrives, since the whole point is
 * a link pasted into another browser — that action is silently discarded: the
 * effect runs, the analytics event lands, and the navigator simply re-applies
 * the existing state. The guest was left sitting on the public announcement
 * page with «Присоединиться»/«Подписаться», i.e. the event pre-registration
 * surface, with no error anywhere. Measured on dev: five `live_detail_redirect`
 * events from QA's own session, every one of them followed by no navigation.
 *
 * Same defect and same fix as CORE-675 (`/u/:userId`) — see
 * `PublicUserProfileScreen`. Rendering the target screen removes the race
 * outright instead of re-timing it, and keeps the shareable
 * `/live/:id?preview=…` URL in the address bar rather than rewriting it to
 * `/guest-room/:id`. Every invite link already handed out keeps working.
 */
export const LiveDetailRoute: React.FC<any> = ({ navigation, route }) => {
    // Prefer the explicit nav param; fall back to `?preview=` in the browser
    // URL so deep links work without changing the route declaration on every
    // consumer. (Moved here verbatim from LiveDetailScreen.)
    const previewToken: string | null = (() => {
        if (route?.params?.preview) return route.params.preview;
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const fromQuery = new URLSearchParams(window.location.search).get('preview');
            if (fromQuery) return fromQuery;
        }
        return null;
    })();

    const id = route?.params?.id;

    // CORE-528 — превью-токен больше не единственное условие входа в комнату
    // ко-хоста. Она запрашивает камеру и микрофон безусловно, поэтому анонима
    // туда пускать нельзя: он приходил посмотреть эфир, а получал системный
    // запрос доступа к камере. Решение вынесено в чистый резолвер.
    const { isAuthenticated } = useAuth();
    const entry = resolveBroadcastGuestEntry({ previewToken, isAuthenticated });

    // Cohost-invite funnel entry. BE-spec §3:
    // docs/improvements/observability-server-events.md — pairs with the
    // server-side `cohost.invite_token_created` and the cohost-room-side
    // `live_preview_token_link_expired` (fired from GuestRoomScreen when the
    // token comes back 410/expired).
    useEffect(() => {
        if (!previewToken) return;
        analytics.track('live_preview_token_link_opened', { liveId: id, entry: 'live_detail_redirect' });
    }, [previewToken, id]);

    // Отдельное событие на случай, когда по валидной ссылке пришёл аноним:
    // без него «ко-хост не доехал до комнаты» и «ко-хост не был авторизован»
    // выглядят в аналитике одинаково, а это разные проблемы — вторая лечится
    // копирайтом приглашения, а не кодом.
    useEffect(() => {
        if (!previewToken || entry !== 'announcement') return;
        analytics.track('live_preview_token_guest_gated', { liveId: id, reason: 'anonymous' });
    }, [previewToken, entry, id]);

    const guestRoute = useMemo(() => ({
        params: { liveId: Number(id), previewToken: previewToken as string },
    }), [id, previewToken]);

    if (entry === 'cohost_room') {
        return <GuestRoomScreen navigation={navigation as never} route={guestRoute as never} />;
    }

    // Аноним по превью-ссылке видит обычный анонс. Токен остаётся в адресе,
    // а returnTo у showAuthCTA — это pathname + search, поэтому после входа
    // он вернётся сюда же и уже пройдёт в комнату ко-хоста.
    return <LiveDetailScreen navigation={navigation} route={route} />;
};

export default LiveDetailRoute;
